import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { getDefaultRouteForRole, can, canAny } from '../utils/rbac';
import { normalizeRole } from '../constants/roles';

const SocietyContextPrompt = () => {
  return (
    <Paper sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Select a society to continue
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This page is scoped to a society. Use the selector in the top bar.
      </Typography>
    </Paper>
  );
};

const ProtectedRoute = ({
  children,
  permission,
  allowedRoles = [],
  requireSociety = false,
  requireSocietyForRoles = [],
  featureFlag = '',
}) => {
  const { status, role, user, capabilities } = useSelector((state) => state.auth);
  const activeSocietyId = useSelector((state) => state.context.activeSocietyId);
  const normalizedRole = normalizeRole(role);
  const location = useLocation();
  const defaultRoute = getDefaultRouteForRole(role);
  const deniedRouteFallback =
    capabilities?.navigation?.find((item) => item?.path && item.path !== location.pathname)?.path ||
    '/account/profile';

  if (status === 'bootstrapping' || status === 'authenticating') {
    return (
      <Box sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(normalizedRole)) {
    return <Navigate to={defaultRoute === location.pathname ? deniedRouteFallback : defaultRoute} replace />;
  }

  if (permission && ['idle', 'loading'].includes(capabilities?.status)) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const allowedByPermission = Array.isArray(permission)
    ? canAny(role, permission, user, capabilities)
    : can(role, permission, user, capabilities);

  if (permission && !allowedByPermission) {
    return <Navigate to={defaultRoute === location.pathname ? deniedRouteFallback : defaultRoute} replace />;
  }

  if (featureFlag && capabilities?.featureFlags?.[featureFlag] === false) {
    return <Navigate to={defaultRoute === location.pathname ? deniedRouteFallback : defaultRoute} replace />;
  }

  if ((requireSociety || requireSocietyForRoles.includes(normalizedRole)) && !activeSocietyId) {
    return <SocietyContextPrompt />;
  }

  return children;
};

export default ProtectedRoute;
